import * as React from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query'
import { EntityForm } from '@/components/templates/entity-form'
import { EmptyState } from '@/components/ui/empty-state'
import { getJSON, patchJSON, postJSON } from '@/lib/api'
import { FormField } from '@/components/templates/entity-form'
import { Input } from '@/components/ui/input'
import { customValuesFromProfile } from '@/lib/contacts/custom-field-values'
import { contactNameError, customFieldValueError, personEmailError } from '@/lib/contacts/errors'
import { organizationPayload, personPayload } from '@/lib/contacts/payloads'
import type {
  Contact,
  ContactFormState,
  ContactRecordType,
  CustomField,
  CustomFieldsResponse,
  Organization,
  Person,
} from '@/lib/contacts/types'
import {
  ContactRecordTypeField,
  CustomFieldControls,
  NameField,
  PersonContactFields,
  TaxExemptFields,
  PostalAddressFields,
  type SetContactForm,
} from '@/pages/ContactFormFields'

interface PersonResponse {
  person: Person
}

interface OrganizationResponse {
  organization: Organization
}

interface ContactFormPageProps {
  contactId?: number
  contactType?: Contact['contact_type']
  onCancel: () => void
  onSaved: () => void
}

type ContactFormMutationResponse = PersonResponse | OrganizationResponse
type SaveContactMutation = UseMutationResult<ContactFormMutationResponse, Error, void, unknown>

interface ContactFormModel {
  editing: boolean
  currentRecordType: ContactRecordType
  form: ContactFormState
  setForm: SetContactForm
  customFields: CustomField[]
  loading: boolean
  unavailable: boolean
  saveContact: SaveContactMutation
}

export function ContactFormPage(props: ContactFormPageProps) {
  const model = useContactFormModel(props)

  if (model.loading) {
    return <EmptyState title="Loading contact" />
  }

  if (model.unavailable) {
    return <EmptyState title="Contact unavailable" />
  }

  return <ContactFormContent {...model} onCancel={props.onCancel} />
}

function useContactFormModel({ contactId, contactType, onSaved }: ContactFormPageProps) {
  const queryClient = useQueryClient()
  const editing = contactId !== undefined && contactType !== undefined
  const editingRecordType = recordTypeFromContactType(contactType)
  const [form, setForm] = React.useState<ContactFormState>(() =>
    emptyContactForm(editingRecordType),
  )
  const personQuery = useQuery({
    queryKey: ['person', contactId],
    queryFn: () => getJSON<PersonResponse>(`/api/v1/persons/${contactId}`),
    enabled: editing && contactType === 'Person',
    retry: false,
  })
  const organizationQuery = useQuery({
    queryKey: ['organization', contactId],
    queryFn: () => getJSON<OrganizationResponse>(`/api/v1/organizations/${contactId}`),
    enabled: editing && contactType === 'Organization',
    retry: false,
  })
  const currentRecordType = editing ? editingRecordType : form.recordType
  const customFieldsQuery = useQuery({
    queryKey: ['custom-fields', currentRecordType],
    queryFn: () =>
      getJSON<CustomFieldsResponse>(`/api/v1/custom_fields?contact_type=${currentRecordType}`),
    retry: false,
  })
  const saveContact = useSaveContact({
    contactId,
    currentRecordType,
    editing,
    form,
    onSaved,
    queryClient,
  })

  useHydrateContactForm(personQuery.data, organizationQuery.data, setForm)

  return {
    editing,
    currentRecordType,
    form,
    setForm,
    customFields: customFieldsQuery.data?.custom_fields ?? [],
    loading: editing && (personQuery.isLoading || organizationQuery.isLoading),
    unavailable: editing && (personQuery.isError || organizationQuery.isError),
    saveContact,
  }
}

function useSaveContact({
  contactId,
  currentRecordType,
  editing,
  form,
  onSaved,
  queryClient,
}: {
  contactId?: number
  currentRecordType: ContactRecordType
  editing: boolean
  form: ContactFormState
  onSaved: () => void
  queryClient: QueryClient
}) {
  return useMutation<ContactFormMutationResponse, Error>({
    mutationFn: () => saveContactRequest({ contactId, currentRecordType, editing, form }),
    onSuccess: async () => {
      await invalidateSavedContactQueries(queryClient, { contactId, currentRecordType, editing })
      onSaved()
    },
  })
}

function useHydrateContactForm(
  personResponse: PersonResponse | undefined,
  organizationResponse: OrganizationResponse | undefined,
  setForm: SetContactForm,
) {
  React.useEffect(() => {
    if (!personResponse) return

    setForm(personFormState(personResponse.person))
  }, [personResponse, setForm])

  React.useEffect(() => {
    if (!organizationResponse) return

    setForm(organizationFormState(organizationResponse.organization))
  }, [organizationResponse, setForm])
}

function ContactFormContent({
  currentRecordType,
  customFields,
  editing,
  form,
  onCancel,
  saveContact,
  setForm,
}: ContactFormModel & { onCancel: () => void }) {
  const emailError =
    currentRecordType === 'person' ? personEmailError(saveContact.error) : undefined
  const nameError = contactNameError(saveContact.error)
  const customFieldError = customFieldValueError(saveContact.error)

  return (
    <EntityForm
      title={editing ? 'Edit contact' : 'New contact'}
      className="mx-0"
      submitLabel={editing ? 'Save contact' : 'Create contact'}
      error={contactFormError(emailError, customFieldError)}
      submitting={saveContact.isPending}
      onCancel={onCancel}
      onSubmit={(event) => {
        event.preventDefault()
        saveContact.mutate()
      }}
    >
      <ContactRecordTypeField
        currentRecordType={currentRecordType}
        editing={editing}
        setForm={setForm}
      />
      <NameField
        name={form.name}
        nameError={nameError}
        currentRecordType={currentRecordType}
        setForm={setForm}
      />
      <FormField label="VAT Number" htmlFor="contact-vat-number">
        <Input
          id="contact-vat-number"
          value={form.vatNumber ?? ''}
          onChange={(event) =>
            setForm((current) => ({ ...current, vatNumber: event.target.value }))
          }
        />
      </FormField>
      {currentRecordType === 'person' && (
        <PersonContactFields
          email={form.email}
          emailError={emailError}
          phone={form.phone}
          setForm={setForm}
        />
      )}
      <TaxExemptFields
        taxExempt={form.taxExempt}
        taxExemptNote={form.taxExemptNote}
        taxExemptReason={form.taxExemptReason ?? 'exempt'}
        setForm={setForm}
      />
      <PostalAddressFields form={form} setForm={setForm} />
      <CustomFieldControls
        customFields={customFields}
        customFieldValues={form.customFieldValues}
        setForm={setForm}
      />
    </EntityForm>
  )
}

function saveContactRequest({
  contactId,
  currentRecordType,
  editing,
  form,
}: {
  contactId?: number
  currentRecordType: ContactRecordType
  editing: boolean
  form: ContactFormState
}) {
  if (currentRecordType === 'organization') {
    const payload = { organization: organizationPayload(form) }
    return editing
      ? patchJSON<OrganizationResponse>(`/api/v1/organizations/${contactId}`, payload)
      : postJSON<OrganizationResponse>('/api/v1/organizations', payload)
  }

  const payload = { person: personPayload(form) }
  return editing
    ? patchJSON<PersonResponse>(`/api/v1/persons/${contactId}`, payload)
    : postJSON<PersonResponse>('/api/v1/persons', payload)
}

async function invalidateSavedContactQueries(
  queryClient: QueryClient,
  {
    contactId,
    currentRecordType,
    editing,
  }: { contactId?: number; currentRecordType: ContactRecordType; editing: boolean },
) {
  await queryClient.invalidateQueries({ queryKey: ['contacts'] })
  if (editing && currentRecordType === 'person') {
    await queryClient.invalidateQueries({ queryKey: ['person', contactId] })
  }
  if (editing && currentRecordType === 'organization') {
    await queryClient.invalidateQueries({ queryKey: ['organization', contactId] })
  }
}

function emptyContactForm(recordType: ContactRecordType): ContactFormState {
  return {
    recordType,
    name: '',
    email: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    region: '',
    postalCode: '',
    countryCode: '',
    taxExempt: false,
    taxExemptNote: '',
    vatNumber: '',
    taxExemptReason: 'exempt',
    customFieldValues: {},
  }
}

function personFormState(person: Person): ContactFormState {
  return {
    recordType: 'person',
    name: person.name,
    email: person.email ?? '',
    phone: person.phone ?? '',
    addressLine1: person.address_line1 ?? '',
    addressLine2: person.address_line2 ?? '',
    city: person.city ?? '',
    region: person.region ?? '',
    postalCode: person.postal_code ?? '',
    countryCode: person.country_code ?? '',
    taxExempt: person.tax_exempt ?? false,
    taxExemptNote: person.tax_exempt_note ?? '',
    vatNumber: person.vat_number ?? '',
    taxExemptReason: person.tax_exempt_reason ?? 'exempt',
    customFieldValues: customValuesFromProfile(person.custom_field_values ?? []),
  }
}

function organizationFormState(organization: Organization): ContactFormState {
  return {
    recordType: 'organization',
    name: organization.name,
    email: '',
    phone: '',
    addressLine1: organization.address_line1 ?? '',
    addressLine2: organization.address_line2 ?? '',
    city: organization.city ?? '',
    region: organization.region ?? '',
    postalCode: organization.postal_code ?? '',
    countryCode: organization.country_code ?? '',
    taxExempt: organization.tax_exempt ?? false,
    taxExemptNote: organization.tax_exempt_note ?? '',
    vatNumber: organization.vat_number ?? '',
    taxExemptReason: organization.tax_exempt_reason ?? 'exempt',
    customFieldValues: customValuesFromProfile(organization.custom_field_values ?? []),
  }
}

function recordTypeFromContactType(contactType?: Contact['contact_type']): ContactRecordType {
  return contactType === 'Organization' ? 'organization' : 'person'
}

function contactFormError(emailError: string | undefined, customFieldError: string | undefined) {
  if (emailError) return 'This email is already used by another contact in this association.'
  if (customFieldError) return customFieldError

  return undefined
}
